import { ProspectActionKind, ProspectStatus } from "@prisma/client";
import Avatar from "@/app/(components)/generics/Avatar/Avatar";
import IconCheck from "@/app/(components)/generics/Icons/IconCheck";
import IconClose from "@/app/(components)/generics/Icons/IconClose";
import { Tooltip, TooltipPosition, TooltipType } from "@/app/(components)/generics/Tootlip/Tooltip";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { ProspectActionTypeItem } from "@/app/types/prospect/ProspectAction";
import { ProspectTableRow } from "@/app/types/prospect/ProspectTableRow";
import { fitScoreClass } from "@/app/utils/UFitScore";
import { UStyles } from "@/app/utils/UStyles";
import ProspectActionSelect from "../ProspectActions/ProspectActionSelect";
import styles from "./ProspectList.module.css";

type Props = {
  prospect: ProspectTableRow;
  lang: string;
  onClick?: (prospectId: number, fullName: string) => void;
  onQualify?: (prospectId: number, previous: ProspectStatus, scoreFit: number) => void;
  onReject?: (prospectId: number, previous: ProspectStatus) => void;
  showStatus?: boolean;
  actionTypes?: ProspectActionTypeItem[];
  onSelectAction?: (
    prospectId: number,
    kind: ProspectActionKind,
    type?: ProspectActionTypeItem,
  ) => void;
  onCreateActionType?: (prospectId: number) => void;
  onOpenHistory?: (prospectId: number, fullName: string) => void;
};

export default function ProspectRow(props: Props) {
  const T = useT();
  const {
    prospect,
    lang,
    onClick,
    onQualify,
    onReject,
    showStatus,
    actionTypes = [],
    onSelectAction,
    onCreateActionType,
    onOpenHistory,
  } = props;

  const hasActionSelect =
    prospect.status === ProspectStatus.QUALIFIED && (!!onSelectAction || !!onOpenHistory);
  const hasActions = !!(onQualify || onReject || showStatus || hasActionSelect);
  const level = fitScoreClass(prospect.scoreFit);
  const subtitle = [prospect.role, prospect.companyName, prospect.location]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className={UStyles.classNames(styles.row, onClick && styles.rowClickable)}
      onClick={onClick ? () => onClick(prospect.id, prospect.fullName) : undefined}
    >
      <div className={styles.realProfile}>
        <Avatar url={prospect.avatarUrl} name={prospect.fullName} size={36} />
        <div className={styles.realProspect}>
          <span className={styles.realName}>{prospect.fullName}</span>
          {subtitle && <span className={styles.realSub}>{subtitle}</span>}
        </div>
      </div>

      <div className={styles.realSignals}>
        {prospect.detectedSignals.slice(0, 4).map((signal, index) => (
          <span key={index} className={styles.realSignal}>
            {signal}
          </span>
        ))}
      </div>

      <div className={styles.realScoreCell}>
        <span
          className={UStyles.classNames(
            styles.score,
            styles[`score${level.charAt(0).toUpperCase()}${level.slice(1)}`],
          )}
        >
          {prospect.scoreFit}
        </span>
      </div>

      {hasActions && (
        <div className={styles.actionsCell} onClick={(event) => event.stopPropagation()}>
          {hasActionSelect ? (
            <ProspectActionSelect
              prospect={prospect}
              lang={lang}
              actionTypes={actionTypes}
              onSelect={(kind, type) => onSelectAction?.(prospect.id, kind, type)}
              onCreateType={onCreateActionType ? () => onCreateActionType(prospect.id) : undefined}
              onOpenHistory={
                onOpenHistory ? () => onOpenHistory(prospect.id, prospect.fullName) : undefined
              }
            />
          ) : prospect.status === ProspectStatus.QUALIFIED ? (
            <span className={UStyles.classNames(styles.statusTag, styles.statusTagQualified)}>
              <IconCheck color="currentColor" />
              {T(TKey.PROSPECT_SEARCH_STATUS_QUALIFIED)}
            </span>
          ) : prospect.status === ProspectStatus.REJECTED ? (
            <span className={UStyles.classNames(styles.statusTag, styles.statusTagRejected)}>
              <IconClose />
              {T(TKey.PROSPECT_SEARCH_STATUS_REJECTED)}
            </span>
          ) : (
            <>
              {onReject && (
                <Tooltip
                  className={styles.rejectTip}
                  type={TooltipType.NONE}
                  position={TooltipPosition.TOP}
                  content={T(TKey.PROSPECT_SEARCH_REJECT)}
                  portal={true}
                  trigger={
                    <button
                      type="button"
                      className={UStyles.classNames(styles.actionBtn, styles.actionReject)}
                      onClick={() => onReject(prospect.id, prospect.status)}
                    >
                      <IconClose />
                    </button>
                  }
                />
              )}
              {onQualify && (
                <Tooltip
                  className={styles.qualifyTip}
                  type={TooltipType.NONE}
                  position={TooltipPosition.TOP}
                  content={T(TKey.PROSPECT_SEARCH_QUALIFY)}
                  portal={true}
                  trigger={
                    <button
                      type="button"
                      className={UStyles.classNames(styles.actionBtn, styles.actionQualify)}
                      onClick={() => onQualify(prospect.id, prospect.status, prospect.scoreFit)}
                    >
                      <IconCheck color="currentColor" />
                    </button>
                  }
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
